import React, { useState, useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import MovieComponent from './MovieComponent';
import Loader from './Loader';
import useMovieSearch from './useMovieSearch';
import NotFound from './NotFound';
import { ThemeContext } from '../context/themeContext';

const MovieReview = () => {
  const { theme } = React.useContext(ThemeContext);
  const isDarkTheme = theme === 'dark';
  const [query, setQuery] = useState('');
  const [inputText, setInputText] = useState('');
  const [page, setPage] = useState(0);
  const { movies, hasMore, loading, error } = useMovieSearch(query, page);
  const observer = useRef();
  const lastMovieElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );
  const updateQuery = (data) => {
    // A search query api call.
    setQuery(data);
    setPage(0);
  };
  const delayedQuery = useCallback(
    debounce((q) => updateQuery(q), 500),
    [query],
  );
  const onChangeInput = (e) => {
    setInputText(e.target.value);
  };
  const getImageUrl = (url) => {
    return url ? 'https://www.nytimes.com/'+url : 'https://source.unsplash.com/user/erondu/440x293';
  }
  useEffect(() => {
    delayedQuery(inputText);
    // Cancel the debounce on useEffect cleanup.
    return delayedQuery.cancel;
  }, [inputText, delayedQuery]);

  return (
    <section
      className={classNames(
        'body-font overflow-hidden',
        { 'text-gray-600': !isDarkTheme },
        { 'text-gray-400 bg-gray-900': isDarkTheme },
      )}
    >
      <div className="container px-5 py-8 mx-auto">
        <div className="flex flex-wrap w-full mb-8 items-center">
          <div className="lg:w-1/2 w-full mb-6 lg:mb-0">
            <h1
              className={classNames(
                'sm:text-3xl text-2xl font-medium title-font mb-2 text-center',
                { 'text-gray-900': !isDarkTheme },
                { 'text-white': isDarkTheme },
              )}
            >
              <label htmlFor="inputTextSearch">Search Movie for Review</label>
            </h1>
          </div>
          <p
            className={classNames(
              'lg:w-1/2 w-full leading-relaxed',
              { 'text-gray-700': !isDarkTheme },
              { 'text-gray-400 text-opacity-90': isDarkTheme },
            )}
          >
            <input
              type="text"
              className={classNames(
                'w-full rounded border focus:ring-2 focus:bg-transparent focus:border-indigo-500 text-base outline-none py-1 px-3 leading-8',
                {
                  'bg-gray-100 bg-opacity-50 border-gray-300 focus:ring-indigo-200 text-gray-700': !isDarkTheme,
                },
                {
                  'bg-gray-800 bg-opacity-40 border-gray-700 focus:ring-indigo-900 text-gray-100': isDarkTheme,
                },
              )}
              placeholder="Search Movie"
              value={inputText}
              id="inputTextSearch"
              onChange={onChangeInput}
              autoComplete="off"
            />
          </p>
        </div>
        <div className="flex flex-wrap -m-4">
          {movies.map((movie, index) => {
            if (movies.length === index + 1) {
              return (
                <div ref={lastMovieElementRef} className="xl:w-1/4 md:w-1/2 p-4" key={index}>
                  <MovieComponent
                    key={movie._id}
                    movieUrl={movie.uri}
                    title={movie.headline?.main || movie.abstract || 'N.A.'}
                    description={movie.snippet || 'N.A.'}
                    rating={movie.news_desk}
                    imageUrl={
                      getImageUrl(movie.multimedia?.[0]?.url)
                    }
                    publicationDate={movie.pub_date}
                    updatedDate={movie.pub_date}
                    byline={movie.byline?.original}
                    isDarkTheme={isDarkTheme}
                    viewMoreUrl={movie.web_url}
                  />
                </div>
              );
            }
            return (
              <div className="xl:w-1/4 md:w-1/2 p-4" key={`${index}-parent`}>
                <MovieComponent
                  key={index}
                  movieUrl={movie.uri}
                  title={movie.headline?.main || movie.abstract || 'N.A.'}
                  description={movie.snippet || 'N.A.'}
                  rating={movie.news_desk}
                  imageUrl={
                    getImageUrl(movie.multimedia?.[0]?.url)
                  }
                  publicationDate={movie.pub_date}
                  updatedDate={movie.pub_date}
                  byline={movie.byline?.original}
                  isDarkTheme={isDarkTheme}
                  viewMoreUrl={movie.web_url}
                />
              </div>
            );
          })}
        </div>
        {loading && !error && <Loader isDarkTheme={isDarkTheme} />}
        {(error || (movies.length === 0 && !loading)) && (
          <NotFound isDarkTheme={isDarkTheme} error={error} />
        )}
      </div>
    </section>
  );
};

export default MovieReview;
